import { cn } from '@repo/lib/cn';
import { Mic, Pause, Play, Square } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button, ButtonGroup } from '@/components/ui/button';
import { useVoiceRecorder } from '@/hooks/voice-recorder';

import type { VoiceRecorderProps } from '.';

import { AudioVisualizer } from '.';

export function VoiceRecorder(voiceRecorderProps: VoiceRecorderProps) {
  const { className, onRecordingPause, onRecordingStop, value, disabled } = voiceRecorderProps;
  const [state, actions] = useVoiceRecorder({
    onRecordingPause,
    onRecordingStop,
    value,
  });

  const { isRecording, isPaused, recordingTime, analyserNode } = state;
  const { startRecording, pauseRecording, stopRecording, formatTime } = actions;

  const [stoppedTime, setStoppedTime] = useState<number | null>(null);

  useEffect(() => {
    if (!value) {
      setStoppedTime(null);
    }
  }, [value]);

  useEffect(() => {
    if (isRecording) {
      setStoppedTime(null);
    }
  }, [isRecording]);

  const handleStartRecording = () => {
    setStoppedTime(null);
    startRecording();
  };

  const handleStopRecording = () => {
    setStoppedTime(recordingTime);
    stopRecording();
  };

  const displayTime: number = isRecording || isPaused ? recordingTime : (stoppedTime ?? recordingTime);

  return (
    <div className={cn('w-fit space-y-1', className)}>
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 text-2xl">
          {isRecording && <div className="bg-brand size-2 animate-pulse rounded-full"></div>}
          <span className="text-muted-foreground text-sm">{formatTime(displayTime)}</span>
        </div>
      </div>

      <div className="bg-card border-border-primary flex flex-col items-center gap-2 rounded-lg border px-3 py-1 sm:flex-row">
        <AudioVisualizer isRecording={isRecording && !isPaused} analyserNode={analyserNode} className="h-14 w-full" />

        <div className="flex items-center justify-center gap-2">
          {!isRecording ? (
            <Button type={'button'} variant={'secondary'} onClick={handleStartRecording} disabled={disabled}>
              <Mic />
            </Button>
          ) : (
            <ButtonGroup>
              <Button variant={'outline'} type={'button'} onClick={pauseRecording} disabled={disabled}>
                {isPaused ? <Play /> : <Pause />}
              </Button>

              <Button variant={'outline'} type={'button'} onClick={handleStopRecording} disabled={disabled}>
                <Square />
              </Button>
            </ButtonGroup>
          )}
        </div>
      </div>
    </div>
  );
}
